import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BellIcon,
  CaretDownIcon,
  CheckIcon,
  HouseIcon,
  ListIcon,
  MagnifyingGlassIcon,
  BandaidsIcon,
  PlusIcon,
  QuestionIcon,
  SmileyIcon,
  UserCircleIcon,
  UserIcon,
} from "@phosphor-icons/react";
import { sanitizarEmail, sanitizarTexto } from "@encuentrayayuda/shared/lib/sanitizar";
import { ahoraLocal, hoyLocal } from "@encuentrayayuda/shared/lib/fechas";
import { COLOR_CABELLO, COLOR_OJOS, CONTEXTURA, ESTADO_PERSONA, RELACION, SEXO, TEZ_PIEL, crearPersonaSchema } from "@encuentrayayuda/shared/schemas/persona";
import ModalCamposFaltantes from "./ModalCamposFaltantes.jsx";
import FotosMascotaForm from "./FotosMascotaForm.jsx";
import ConfirmarMascotaForm from "./ConfirmarMascotaForm.jsx";
import ReportePublicado from "./ReportePublicado.jsx";
import Turnstile from "./Turnstile.jsx";
import UbicacionPersonaForm from "./UbicacionPersonaForm.jsx";
import { guardarBorrador, leerBorrador, limpiarBorrador } from "./lib/borradores.js";
import { publicarPersona } from "./lib/publicarMascota.js";

const pasos = ["Información y características", "Dónde y cuándo", "Fotos", "Confirmar"];
const relaciones = {
  FAMILIAR: "Familiar",
  AMIGO: "Amigo(a)",
  COLEGA: "Colega",
  DESCONOCIDO: "No la conozco",
};
const etiquetasSexo = { MUJER: "Femenino", HOMBRE: "Masculino", NO_ESPECIFICA: "Otro" };
const etiquetasContextura = { DELGADA: "Delgada", MEDIANA: "Mediana", ROBUSTA: "Robusta" };
const etiquetasTez = { BLANCA: "Clara", TRIGUENA: "Trigueña", OSCURA: "Oscura" };
const cabellos = [
  ["NEGRO", "Negro", "#272727"], ["CASTANO_OSCURO", "Castaño oscuro", "#493124"],
  ["CASTANO", "Castaño", "#704a32"], ["CASTANO_CLARO", "Castaño claro", "#a6754d"],
  ["RUBIO", "Rubio", "#d6b56e"], ["RUBIO_OSCURO", "Rubio oscuro", "#a3834b"],
  ["PELIRROJO", "Pelirrojo", "#a74829"], ["CANOSO", "Canoso", "#aeb2b5"],
  ["TENIDO", "Teñido", "linear-gradient(135deg, #7746ae, #dc5e9f, #2b9dba)"], ["CALVO", "Calvo", "#e4bd99"],
];
const etiquetasOjos = {
  MARRON_OSCURO: "Marrón oscuro", MARRON: "Marrón", MIEL: "Miel",
  VERDE: "Verde", AZUL: "Azul", GRIS: "Gris", NEGRO: "Negro",
  HETEROCROMIA: "Dos colores diferentes", NO_ESPECIFICA: "No se sabe",
};
const estadosPersona = [
  { value: "BIEN", label: "Está bien", Icon: SmileyIcon },
  { value: "HERIDA", label: "Herida", Icon: BandaidsIcon },
  { value: "DESORIENTADA", label: "Desorientada", Icon: QuestionIcon },
];
function datosIniciales() {
  return {
    nombrePersona: "", relacion: "", sexo: "", edadAproximada: "", estaturaAproximada: "",
    contextura: "", tezPiel: "", colorCabello: "", coloresCabello: [], colorOjos: "",
    senalesParticulares: "", vestimenta: "", informacionAdicional: "", estadoPersona: "",
    lugarEncontrado: "", nombreHospital: "",
    fechaEvento: hoyLocal(), horaEvento: ahoraLocal(),
    fechaMaxima: hoyLocal(), latEvento: null, lngEvento: null, precisionGps: null,
    direccionEvento: "", puntoReferencia: "", ciudad: null, departamento: null,
  };
}

function cargarBorrador(storageKey) {
  try {
    const guardado = leerBorrador(storageKey);
    return guardado ? { ...datosIniciales(), ...JSON.parse(guardado), fechaMaxima: hoyLocal() } : datosIniciales();
  } catch {
    return datosIniciales();
  }
}

function clasePaso(indice, pasoActual) {
  if (indice + 1 === pasoActual) return "is-active";
  if (indice + 1 < pasoActual) return "is-complete";
  return "";
}

function normalizarEstatura(valor) {
  if (!String(valor).trim()) return { texto: "", numero: null, valida: true };
  let numero = Number(String(valor).replace(",", "."));
  if (!Number.isFinite(numero)) return { texto: valor, numero: null, valida: false };
  if (numero >= 30) numero /= 100;
  else if (numero >= 10) numero /= 10;
  const valida = numero >= 0.3 && numero <= 2.6;
  return {
    texto: valida ? numero.toFixed(2).replace(".", ",") : valor,
    numero: valida ? numero : null,
    valida,
  };
}

function construirPayload(datos, contacto, tipo) {
  const payload = {
    tipo,
    reportante: {
      nombreReportante: sanitizarTexto(contacto.nombre),
      telefonoReportante: `${contacto.codigoPais}${contacto.telefono}`,
      emailReportante: sanitizarEmail(contacto.email),
    },
    nombrePersona: sanitizarTexto(datos.nombrePersona) || "Persona sin identificar",
    sexo: datos.sexo,
    relacion: tipo === "ENCONTRADO" ? "DESCONOCIDO" : datos.relacion,
  };
  const ubicacion = { lat: datos.latEvento, lng: datos.lngEvento };
  const momento = new Date(`${datos.fechaEvento}T${datos.horaEvento || "00:00"}`).toISOString();
  if (tipo === "ENCONTRADO") {
    payload.encontro = ubicacion;
    payload.encontradaEn = momento;
    payload.estadoPersona = datos.estadoPersona;
    payload.lugarEncontrado = datos.lugarEncontrado;
    const detalleLugar = sanitizarTexto(datos.nombreHospital);
    if (detalleLugar) payload.nombreHospital = detalleLugar;
  } else {
    payload.perdido = ubicacion;
    payload.ultimaVistaEn = momento;
  }
  const opcionalesTexto = ["contextura", "tezPiel", "colorCabello", "colorOjos", "ciudad", "departamento"];
  for (const campo of opcionalesTexto) if (datos[campo]) payload[campo] = datos[campo];
  if (datos.coloresCabello.length) payload.coloresCabello = datos.coloresCabello;
  if (datos.edadAproximada !== "") payload.edadAproximada = Number(datos.edadAproximada);
  const estatura = normalizarEstatura(datos.estaturaAproximada).numero;
  if (estatura != null) payload.estaturaAproximada = estatura;
  for (const campo of ["senalesParticulares", "vestimenta", "informacionAdicional"]) {
    const valor = sanitizarTexto(datos[campo]);
    if (valor) payload[campo] = valor;
  }
  return payload;
}

export default function PersonaDesaparecidaForm({ onBack, onNavigate, tipo = "DESAPARECIDO" }) {
  const esEncontrada = tipo === "ENCONTRADO";
  const storageKey = `encuentrayayuda:persona-${esEncontrada ? "encontrada" : "desaparecida"}:v1`;
  const [datos, setDatos] = useState(() => cargarBorrador(storageKey));
  const [advertencia, setAdvertencia] = useState(null);
  const [pasoActual, setPasoActual] = useState(1);
  const [fotos, setFotos] = useState([]);
  const [contacto, setContacto] = useState({ nombre: "", codigoPais: "57", telefono: "", email: "" });
  const [publicando, setPublicando] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const [errorEnvio, setErrorEnvio] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [tokenAntiBot, setTokenAntiBot] = useState(null);
  const fotosRef = useRef(fotos);
  const turnstileRef = useRef(null);
  const publicacionRef = useRef({ reporte: null, multimedia: [] });

  useEffect(() => {
    fotosRef.current = fotos;
  }, [fotos]);

  useEffect(() => () => {
    fotosRef.current.forEach((foto) => URL.revokeObjectURL(foto.url));
  }, []);

  useEffect(() => {
    const { latEvento, lngEvento, precisionGps, ...borradorPersistente } = datos;
    guardarBorrador(storageKey, borradorPersistente);
  }, [datos, storageKey]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pasoActual]);

  function actualizar(campo, valor) {
    const camposTexto = ["nombrePersona", "senalesParticulares", "vestimenta", "informacionAdicional"];
    const seguro = camposTexto.includes(campo) ? sanitizarTexto(valor, { recortar: false }) : valor;
    setDatos((actuales) => ({ ...actuales, [campo]: seguro }));
  }

  function actualizarEstatura(valor) {
    actualizar("estaturaAproximada", valor.replace(/[^0-9.,]/g, "").slice(0, 4));
  }

  function formatearEstatura() {
    const resultado = normalizarEstatura(datos.estaturaAproximada);
    if (resultado.valida) actualizar("estaturaAproximada", resultado.texto);
  }

  function alternarCabello(color) {
    if (!COLOR_CABELLO.includes(color)) return;
    setDatos((actuales) => {
      const seleccionados = actuales.coloresCabello.includes(color)
        ? actuales.coloresCabello.filter((item) => item !== color)
        : [...actuales.coloresCabello, color];
      return { ...actuales, coloresCabello: seleccionados, colorCabello: seleccionados[0] ?? "" };
    });
  }

  function continuar(evento) {
    evento.preventDefault();
    const nombrePersona = sanitizarTexto(datos.nombrePersona);
    const faltantes = [];
    if (!esEncontrada && !nombrePersona) faltantes.push("Nombre completo de la persona");
    if (!esEncontrada && !RELACION.includes(datos.relacion)) faltantes.push("Relación contigo");
    if (esEncontrada && !ESTADO_PERSONA.includes(datos.estadoPersona)) faltantes.push("Estado de la persona encontrada");
    if (!SEXO.includes(datos.sexo)) faltantes.push("Sexo");
    const estatura = normalizarEstatura(datos.estaturaAproximada);
    if (!estatura.valida) faltantes.push("Estatura aproximada entre 0,30 y 2,60 m");
    setDatos((actuales) => ({ ...actuales, nombrePersona, estaturaAproximada: estatura.texto }));
    if (faltantes.length) {
      setAdvertencia(faltantes);
      return;
    }
    setPasoActual(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function renderizarPasoPosterior() {
    if (pasoActual === 2) {
      return <UbicacionPersonaForm datos={datos} onChange={setDatos} onBack={() => setPasoActual(1)} onNext={() => setPasoActual(3)} onMissing={setAdvertencia} esEncontrada={esEncontrada} />;
    }
    if (pasoActual === 3) {
      return <FotosMascotaForm fotos={fotos} onChange={setFotos} onBack={() => setPasoActual(2)} onNext={() => setPasoActual(4)} onMissing={setAdvertencia} variante="persona" />;
    }
    return <ConfirmarMascotaForm variante="persona" datos={datos} fotos={fotos} contacto={contacto} onContactChange={setContacto} onBack={() => setPasoActual(3)} onEditStep={setPasoActual} onPublicar={publicar} publicando={publicando} progreso={progreso} errorEnvio={errorEnvio} widgetAntiBot={<Turnstile ref={turnstileRef} onToken={setTokenAntiBot} onError={(mensaje) => setErrorEnvio(new Error(mensaje))} onExpirado={() => setTokenAntiBot(null)} />} />;
  }

  function validarConfirmacion() {
    const nombre = sanitizarTexto(contacto.nombre);
    const email = sanitizarEmail(contacto.email);
    const faltantes = [];
    if (!nombre) faltantes.push("Nombre completo del registrante");
    if (contacto.telefono.length !== 10) faltantes.push("WhatsApp de 10 dígitos");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) faltantes.push("Correo electrónico válido");
    setContacto((actual) => ({ ...actual, nombre, email }));
    if (datos.latEvento == null || datos.lngEvento == null) faltantes.push("Ubicación exacta en el mapa");
    if (!sanitizarTexto(datos.direccionEvento)) faltantes.push("Dirección o referencia");
    if (!fotos.length) faltantes.push("Al menos una foto");
    if (faltantes.length) {
      setAdvertencia(faltantes);
      return null;
    }
    const validacion = crearPersonaSchema.safeParse(construirPayload(datos, { ...contacto, nombre, email }, tipo));
    if (!validacion.success) {
      setAdvertencia([...new Set(validacion.error.issues.map((problema) => problema.message))]);
      return null;
    }
    return validacion.data;
  }

  async function publicar() {
    if (publicando) return;
    const payload = validarConfirmacion();
    if (!payload) return;
    setPublicando(true);
    setErrorEnvio(null);
    setProgreso({ indice: 0, total: fotos.length, porcentaje: 0 });
    try {
      const reporte = await publicarPersona({ payload, fotos, estado: publicacionRef.current, onProgreso: setProgreso, tokenAntiBot });
      limpiarBorrador(storageKey);
      setResultado(reporte);
    } catch (error) {
      setErrorEnvio(error);
      setTokenAntiBot(null);
      turnstileRef.current?.reiniciar();
    } finally {
      setPublicando(false);
    }
  }

  if (resultado) {
    return <div className="report-flow report-flow--person"><ReportePublicado variante="persona" resultado={resultado} datos={datos} fotos={fotos} contacto={contacto} onInicio={onBack} /></div>;
  }

  return (
    <div className="report-flow report-flow--person">
      <header className="report-flow__header"><button type="button" onClick={pasoActual === 1 ? onBack : () => setPasoActual(1)} aria-label="Volver"><ArrowLeftIcon weight="bold" /></button><h1>Reportar persona {esEncontrada ? "encontrada" : "desaparecida"}</h1><span /></header>

      <ol className="stepper stepper--person" aria-label="Progreso del reporte">
        {pasos.map((paso, indice) => <li className={clasePaso(indice, pasoActual)} key={paso}><button type="button" disabled={indice + 1 > pasoActual} onClick={() => indice + 1 <= pasoActual && setPasoActual(indice + 1)}>{indice + 1}</button><strong>{paso}</strong></li>)}
      </ol>

      {pasoActual === 1 ? <>
        <section className="person-intro"><span><UserIcon weight="fill" /></span><h2>{esEncontrada ? <>Cuéntanos sobre<br />la persona encontrada</> : <>Cuéntanos sobre<br />la persona</>}</h2><p>{esEncontrada ? "Tu reporte puede ayudar a que vuelva con su familia." : "Mientras más información brindes, más rápido podremos ayudar a encontrarla."}</p></section>

        <form className="person-first-form" onSubmit={continuar}>
          <fieldset><legend>Tipo de reporte</legend><div className="person-report-types"><button type="button" className={!esEncontrada ? "is-selected" : ""} aria-pressed={!esEncontrada} onClick={() => esEncontrada && onNavigate("persona-desaparecida")}><span><MagnifyingGlassIcon weight="duotone" /></span>Persona desaparecida</button><button type="button" className={esEncontrada ? "is-selected" : ""} aria-pressed={esEncontrada} onClick={() => !esEncontrada && onNavigate("persona-encontrada")}><span><UserIcon weight="duotone" /></span>Persona encontrada</button></div></fieldset>

          {esEncontrada && <fieldset className="person-found-status"><legend>Estado de la persona <b>*</b></legend><div>{estadosPersona.map(({ value, label, Icon }) => <button type="button" className={`person-found-status__${value.toLowerCase()} ${datos.estadoPersona === value ? "is-selected" : ""}`} aria-pressed={datos.estadoPersona === value} onClick={() => actualizar("estadoPersona", value)} key={value}><Icon weight="bold" />{label}</button>)}</div></fieldset>}

          <label>Nombre completo {esEncontrada ? <span className="person-field-note">(si lo conoces)</span> : <b>*</b>}<input type="text" maxLength="80" autoComplete="name" value={datos.nombrePersona} onChange={(evento) => actualizar("nombrePersona", evento.target.value)} placeholder={esEncontrada ? "Ej: Juan Pérez (opcional)" : "Ej: María González López"} /></label>

          {!esEncontrada && <label>¿Cuál es su relación contigo? <b>*</b><span className="person-select"><select value={datos.relacion} onChange={(evento) => actualizar("relacion", evento.target.value)}><option value="">Selecciona una opción</option>{RELACION.map((relacion) => <option value={relacion} key={relacion}>{relaciones[relacion]}</option>)}</select><CaretDownIcon weight="bold" /></span></label>}

          <fieldset><legend>Sexo <b>*</b></legend><div className="person-sex-options">{SEXO.map((sexo) => <button type="button" className={datos.sexo === sexo ? "is-selected" : ""} aria-pressed={datos.sexo === sexo} onClick={() => actualizar("sexo", sexo)} key={sexo}><UserIcon weight={datos.sexo === sexo ? "fill" : "regular"} />{etiquetasSexo[sexo]}</button>)}</div></fieldset>

          <div className="person-measurements"><label>Edad aproximada<input type="number" inputMode="numeric" min="0" max="120" value={datos.edadAproximada} onChange={(evento) => actualizar("edadAproximada", evento.target.value)} placeholder="Ej: 25 años" /></label><label>Estatura aproximada<input type="text" inputMode="decimal" value={datos.estaturaAproximada} onChange={(evento) => actualizarEstatura(evento.target.value)} onBlur={formatearEstatura} placeholder="Ej: 172 o 1,72 m" /><small>También puedes escribirla en centímetros.</small></label></div>

          <label>Contextura<span className="person-select"><select value={datos.contextura} onChange={(evento) => actualizar("contextura", evento.target.value)}><option value="">Selecciona una opción</option>{CONTEXTURA.map((valor) => <option value={valor} key={valor}>{etiquetasContextura[valor]}</option>)}</select><CaretDownIcon weight="bold" /></span></label>

          <label>Tez<span className="person-select"><select value={datos.tezPiel} onChange={(evento) => actualizar("tezPiel", evento.target.value)}><option value="">Selecciona una opción</option>{TEZ_PIEL.map((valor) => <option value={valor} key={valor}>{etiquetasTez[valor]}</option>)}</select><CaretDownIcon weight="bold" /></span></label>

          <fieldset><legend>Color de cabello <span className="person-field-note">Puedes elegir varios</span></legend><div className="person-hair-options">{cabellos.map(([valor, texto, muestra]) => <button type="button" className={datos.coloresCabello.includes(valor) ? "is-selected" : ""} aria-pressed={datos.coloresCabello.includes(valor)} onClick={() => alternarCabello(valor)} key={valor}><i style={{ background: muestra }} />{texto}{datos.coloresCabello.includes(valor) && <CheckIcon weight="bold" />}</button>)}</div></fieldset>

          <label>Color de ojos<span className="person-select"><select value={datos.colorOjos} onChange={(evento) => actualizar("colorOjos", evento.target.value)}><option value="">Selecciona una opción</option>{COLOR_OJOS.map((valor) => <option value={valor} key={valor}>{etiquetasOjos[valor]}</option>)}</select><CaretDownIcon weight="bold" /></span></label>

          <label>Señas particulares<textarea maxLength="150" rows="4" value={datos.senalesParticulares} onChange={(evento) => actualizar("senalesParticulares", evento.target.value)} placeholder="Ej: lunar en la mejilla derecha, tatuaje en el brazo, cicatriz en la frente, usa lentes…" /><small className="person-character-count">{datos.senalesParticulares.length}/150</small></label>

          <label>Vestimenta al momento de {esEncontrada ? "encontrarla" : "desaparecer"}<textarea maxLength="150" rows="4" value={datos.vestimenta} onChange={(evento) => actualizar("vestimenta", evento.target.value)} placeholder="Ej: camiseta blanca, jean azul, chaqueta negra, zapatillas blancas…" /><small className="person-character-count">{datos.vestimenta.length}/150</small></label>

          <label>Información adicional <span className="person-field-note">(opcional)</span><textarea maxLength="150" rows="4" value={datos.informacionAdicional} onChange={(evento) => actualizar("informacionAdicional", evento.target.value)} placeholder="Cualquier detalle que consideres importante y pueda ayudar en la búsqueda…" /><small className="person-character-count">{datos.informacionAdicional.length}/150</small></label>

          <button className="continue-button continue-button--person" type="submit">Siguiente <ArrowRightIcon weight="bold" /></button>
        </form>
      </> : renderizarPasoPosterior()}

      <nav className="flow-nav flow-nav--person" aria-label="Navegación principal"><button type="button" onClick={onBack}><HouseIcon weight="fill" /><span>Inicio</span></button><span  className="nav-pendiente" aria-disabled="true" title="Disponible proximamente"><ListIcon /><span>Reportes</span></span><a className="is-active" href="#reportar"><span className="flow-nav__add"><PlusIcon weight="bold" /></span><span>Reportar</span></a><span  className="nav-pendiente" aria-disabled="true" title="Disponible proximamente"><BellIcon /><span>Alertas</span></span><span  className="nav-pendiente" aria-disabled="true" title="Disponible proximamente"><UserCircleIcon /><span>Mi cuenta</span></span></nav>

      {advertencia && <ModalCamposFaltantes campos={advertencia} onClose={() => setAdvertencia(null)} onReview={() => setAdvertencia(null)} />}
    </div>
  );
}
